import { Injectable } from '@angular/core';
import { CurrentUser } from './models/current-user.model';

@Injectable()
export class StorageService {
  private readonly documentIsAccessible: boolean;

  private storage;
  private window;
  private document: Document;

  constructor() {
    this.window = this.getWindow();
    this.storage = this.window.localStorage;
    this.document = this.window.document;
    this.documentIsAccessible = this.document instanceof Document;
  }

  private getWindow() {
    return (typeof window !== 'undefined') ? window : {localStorage: null, document: null, location: null};
  }

  public getStorageItem(item: string, def: any = null): any {
    const data = this.storage.getItem(item);
    return data ? this.tryToParse(data) : def;
  }

  public setStorageValue(item: string, value: string): void {
    this.storage.setItem(item, value);
  }

  public setStorageObject(item: string, value: Object): void {
    this.storage.setItem(item, JSON.stringify(value));
  }

  public getCurrentUser(): CurrentUser {
    const currentUser = this.getCookie('currentUser');
    return currentUser ? JSON.parse(currentUser) as CurrentUser : null;
    // return this.getStorageItem('currentUser') as CurrentUser;
  }

  public setCurrentUser(value: Object): void {
    const domain = this.window.location ? this.window.location.hostname : null;
    this.setCookie('currentUser', JSON.stringify(value), 43200, '/', domain, false);
    this.setStorageObject('currentUser', value);
  }

  public getCurrentUserToken(): string {
    const currentUser = this.getCurrentUser();
    return currentUser ? currentUser.access_token : null;
  }

  public cleanStorage(): void {
    this.deleteCookie('currentUser');
    this.storage.clear();
  }

  public removeStorageItem(item: String): void {
    this.storage.removeItem(item);
  }

  public getLocaleSettings() {
    const localeSettings = this.getCookie('localeSettings');
    return localeSettings ? JSON.parse(localeSettings) : null;
  }

  public setLocaleSettings(value) {
    this.setCookie('localeSettings', JSON.stringify(value));
  }

  private tryToParse(str): Object | String {
    try {
      return JSON.parse(str);
    } catch (e) {
      return str;
    }
  }

  // From: https://github.com/7leads/ngx-cookie-service/blob/master/lib/cookie-service/cookie.service.ts
  /**
   * @param name Cookie name
   * @returns {boolean}
   */
  public checkCookie(name: string): boolean {
    if (!this.documentIsAccessible) {
      return false;
    }

    name = encodeURIComponent(name);

    const regExp: RegExp = this.getCookieRegExp(name);
    return regExp.test(this.document.cookie);
  }

  /**
   * @param name Cookie name
   * @returns {any}
   */
  public getCookie(name: string): string {
    if (this.documentIsAccessible && this.checkCookie(name)) {
      name = encodeURIComponent(name);

      const regExp: RegExp = this.getCookieRegExp(name);
      const result: RegExpExecArray = regExp.exec(this.document.cookie);

      return decodeURIComponent(result[1]);
    } else {
      return '';
    }
  }

  /**
   * @returns {}
   */
  public getAllCookies(): {} {
    if (!this.documentIsAccessible) {
      return {};
    }

    const cookies: {} = {};
    const document: any = this.document;

    if (document.cookie && document.cookie !== '') {
      const split: Array<string> = document.cookie.split(';');

      for (let i = 0; i < split.length; i += 1) {
        const currentCookie: Array<string> = split[i].split('=');

        currentCookie[0] = currentCookie[0].replace(/^ /, '');
        cookies[decodeURIComponent(currentCookie[0])] = decodeURIComponent(currentCookie[1]);
      }
    }

    return cookies;
  }

  /**
   * @param name     Cookie name
   * @param value    Cookie value
   * @param expires  Number of days until the cookies expires or an actual `Date`
   * @param path     Cookie path
   * @param domain   Cookie domain
   * @param secure   Secure flag
   * @param sameSite OWASP samesite token `Lax` or `Strict`
   */
  setCookie(name: string,
            value: string,
            expires?: number | Date,
            path?: string,
            domain?: string,
            secure?: boolean,
            sameSite?: 'Lax' | 'Strict'): void {
    if (!this.documentIsAccessible) {
      return;
    }

    let cookieString: string = encodeURIComponent(name) + '=' + encodeURIComponent(value) + ';';

    if (expires) {
      if (typeof expires === 'number') {
        const dateExpires: Date = new Date(new Date().getTime() + expires * 1000 * 60 * 60 * 24);

        cookieString += 'expires=' + dateExpires.toUTCString() + ';';
      } else {
        cookieString += 'expires=' + expires.toUTCString() + ';';
      }
    }

    if (path) {
      cookieString += 'path=' + path + ';';
    }

    if (domain) {
      cookieString += 'domain=' + domain + ';';
    }

    if (secure) {
      cookieString += 'secure;';
    }

    if (sameSite) {
      cookieString += 'sameSite=' + sameSite + ';';
    }

    this.document.cookie = cookieString;
  }

  /**
   * @param name   Cookie name
   * @param path   Cookie path
   * @param domain Cookie domain
   */
  deleteCookie(name: string, path?: string, domain?: string): void {
    if (!this.documentIsAccessible) {
      return;
    }

    this.setCookie(name, '', new Date('Thu, 01 Jan 1970 00:00:01 GMT'), path, domain);
  }

  /**
   * @param path   Cookie path
   * @param domain Cookie domain
   */
  deleteAllCookies(path?: string, domain?: string): void {
    if (!this.documentIsAccessible) {
      return;
    }

    const cookies: any = this.getAllCookies();

    for (const cookieName in cookies) {
      if (cookies.hasOwnProperty(cookieName)) {
        this.deleteCookie(cookieName, path, domain);
      }
    }
  }

  /**
   * @param name Cookie name
   * @returns {RegExp}
   */
  private getCookieRegExp(name: string): RegExp {
    const escapedName: string = name.replace(/([\[\]\{\}\(\)\|\=\;\+\?\,\.\*\^\$])/ig, '\\$1');

    return new RegExp('(?:^' + escapedName + '|;\\s*' + escapedName + ')=(.*?)(?:;|$)', 'g');
  }
}



// WEBPACK FOOTER //
// ./src/app/storage.service.ts